/**
 * คิวข้อขัดแย้งของ Offline Sync — รูปร่างข้อมูลตาม payload contract ที่ freeze แล้ว
 * (`docs/API_CONTRACT.md` — "Offline Sync — Conflict Queue Payload Contract", D-077)
 *
 * ไฟล์นี้เป็นภาพสะท้อนของเอกสารนั้นเท่านั้น ไม่ใช่ที่ตัดสินใจเพิ่มเติมเอง
 *
 * **สิ่งที่ไม่มีอยู่ในรูปนี้คือตัว contract เอง**: ไม่มี `payload` ของคำสั่ง ไม่มี
 * ปริมาณ และไม่มีธงใดที่แปลว่า "สั่งให้ลงบัญชีเลย" เพราะการปิด conflict คือการ
 * **บันทึกการตัดสินของคน** ไม่ใช่การ retry คำสั่งเดิม (business rule 5, D-077) —
 * ถ้าเหตุถูกแก้แล้วและงานต้องลงจริง ต้องสแกนใหม่เป็นคำสั่งใหม่ (`client_event_id`
 * ใหม่) ผ่าน `POST /v1/offline-sync/commands` ซึ่งตรวจ invariant ใหม่ทั้งชุด
 */

/**
 * รหัสโมดูลที่ทั้งสองเส้นทางถูก gate ด้วย — `SHOP_FLOOR` ไม่ใช่โมดูลของตัวเอง
 *
 * Phase 1 ไม่มีโมดูล "OFFLINE_SYNC" ให้ department ใดถือได้ และ `schemas.py` ฝั่ง
 * เซิร์ฟเวอร์ก็ประกาศตรง ๆ ว่า `OFFLINE_SYNC_MODULE = SHOP_FLOOR_MODULE` — conflict
 * ที่อยู่ในคิวคือคำสั่งหน้างานที่เซิร์ฟเวอร์ปฏิเสธ จึงเป็นเรื่องของโมดูลเดียวกับที่
 * ออกคำสั่งนั้นมา ค่านี้ต้องเท่ากับด่านจริงฝั่งเซิร์ฟเวอร์เสมอ
 */
export const SHOP_FLOOR_MODULE = 'SHOP_FLOOR';

/* --------------------------------------------------------------- outcome -- */

/** คนดูแล้วจัดการเรียบร้อย — **ไม่ได้แปลว่าคำสั่งถูก post ในภายหลัง** (D-077) */
export const CONFLICT_RESOLVED = 'RESOLVED';

/** คนดูแล้วตัดสินว่าการปฏิเสธนั้นถูกต้อง คำสั่งนี้จะไม่ถูกทำเลย */
export const CONFLICT_REJECTED = 'REJECTED';

export const CONFLICT_OUTCOMES = [CONFLICT_RESOLVED, CONFLICT_REJECTED] as const;

export type ConflictOutcome = (typeof CONFLICT_OUTCOMES)[number];

/**
 * `details.rule` ของ `409` ที่การปิดซ้ำได้รับ (D-077)
 *
 * conflict ที่ปิดแล้วบันทึกไว้ว่า **ใคร**ตัดสินและ**เมื่อไร** การตัดสินครั้งที่สอง
 * จะทับครั้งแรกทิ้ง เซิร์ฟเวอร์จึงไม่เขียนอะไรเลยและตอบด้วย rule นี้
 */
export const CONFLICT_ALREADY_RESOLVED = 'CONFLICT_ALREADY_RESOLVED';

/* -------------------------------------------------------------- resource -- */

/**
 * หนึ่งแถวของคิว — แปดฟิลด์ตรงตาม contract ไม่ขาดไม่เกิน
 *
 * `offline_sync_conflict` **ไม่มีคอลัมน์ status** (D-074) เปิด/ปิดจึงอ่านจาก
 * `resolved_at` เพียงอย่างเดียว: ฟิลด์การปิดทั้งสี่ติดมาทุกแถวเสมอ และเป็น `null`
 * เมื่อยังไม่มีใครตัดสิน — client ที่ทึกทักว่าฟิลด์เหล่านี้หายไปแปลว่า "ยังเปิดอยู่"
 * จะอ่าน payload ที่ผิดรูปเป็นคิวที่ยังไม่มีใครดู
 */
export interface OfflineSyncConflict {
  readonly conflict_id: string;
  /**
   * คำสั่งที่ conflict นี้พูดถึง — **ไม่ unique**: คำสั่งที่ถูกส่งใหม่หลังการแก้
   * บางส่วนอาจสร้าง conflict ใบที่สองได้โดยชอบ (D-074) สองแถวจึงอ้างคำสั่งเดียวกันได้
   */
  readonly client_event_id: string;
  /** `STAGING_TRANSFER`/`SCAN_IN`/`SCAN_OUT` — join มาจากแถวของคำสั่ง */
  readonly command_type: string;
  /** rule token ที่เซิร์ฟเวอร์ปฏิเสธคำสั่งนี้ไว้ (เช่น `NO_OPEN_CONFIRMATION`) */
  readonly reason: string;
  /** `RESOLVED`/`REJECTED` เมื่อมีคนตัดสินแล้ว, `null` ระหว่างที่ยังเปิดอยู่ */
  readonly resolution: string | null;
  readonly resolved_by_employee_id: string | null;
  readonly resolved_at: string | null;
  /** เวลาที่ conflict ถูกบันทึก — คีย์เรียงลำดับของคิว (เก่าสุดอยู่บน) */
  readonly created_at: string;
}

/**
 * `{ items, next_cursor }` — คำตอบของ `GET /v1/offline-sync/conflicts`
 *
 * **`items` ว่างคือ `200` ปกติ ไม่ใช่ error** — เป็นวันที่ไม่มีอะไรผิดพลาด
 */
export interface OfflineSyncConflictPage {
  readonly items: readonly OfflineSyncConflict[];
  readonly next_cursor: string | null;
}

/**
 * พารามิเตอร์ของการ list
 *
 * `resolved` ไม่ใช่ตัวกรองที่ "เพิ่มของ" แต่เป็นการสลับมุมมองทั้งชุด: `false`
 * (ค่าเริ่มต้นฝั่งเซิร์ฟเวอร์) คือคิวที่ยังไม่มีใครตัดสิน ส่วน `true` คือประวัติที่
 * ปิดไปแล้วซึ่งอ่านอย่างเดียว
 */
export interface OfflineSyncConflictQuery {
  readonly resolved?: boolean;
  readonly limit?: number;
  readonly cursor?: string;
}
